/**
 * Apple Pay contact extraction.
 *
 * Apple can return a shipping contact that is missing the postal address even
 * though the payment request asks for `postalAddress`. The address fields used
 * to read `shippingContact` only and default to empty, so those orders were
 * charged and created with nothing to ship to (DEV-5488).
 *
 * V4 (`utils/applePay/`) and V5 hold identical copies of this helper — each
 * template is a self-contained Vite app with no shared package. Keep all three
 * in sync, and see `applePayContact.test.js` for the behaviour they must hold.
 */

export const MISSING_ADDRESS_MESSAGE =
  "We need a complete shipping address to deliver your order. Please select or add one and try again.";

const text = (value) => (value ?? "").trim();

const lines = (contact) => (contact?.addressLines ?? []).map(text).filter(Boolean);

/** The postal address of a single contact, as a unit. */
const addressOf = (contact) => ({
  lines: lines(contact),
  city: text(contact?.locality),
  state: text(contact?.administrativeArea),
  zip: text(contact?.postalCode),
  country: text(contact?.countryCode),
});

export function buildApplePayCustomer(payment) {
  const shipping = payment?.shippingContact;
  const billing = payment?.billingContact;

  // The address is taken from one contact or the other, never merged field by
  // field: a street from billing combined with a city from shipping produces a
  // plausible-looking address that passes validation and ships nowhere. That is
  // worse than an empty address, because `billShipSame` then applies the hybrid
  // to billing too and it fails AVS as well.
  const shippingAddress = addressOf(shipping);
  const address = shippingAddress.lines.length > 0 ? shippingAddress : addressOf(billing);

  return {
    // Name, phone and email carry no such coupling, so they still fall back
    // individually — a shipping contact routinely omits one of them.
    first_name: text(shipping?.givenName) || text(billing?.givenName),
    last_name: text(shipping?.familyName) || text(billing?.familyName),
    phone: text(shipping?.phoneNumber) || text(billing?.phoneNumber),
    email: text(shipping?.emailAddress) || text(billing?.emailAddress),
    address: address.lines[0] || "",
    address2: address.lines[1] || "",
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country,
  };
}

/**
 * Returns the names of the shipping fields an order cannot be fulfilled
 * without. A postal code is not required on its own: not every country issues
 * one, and blocking on a missing phone would cost more sales than it saves.
 */
export function missingShippingFields(customer) {
  const missing = [];

  if (!customer.address) {
    missing.push("address");
  }
  if (!customer.country) {
    missing.push("country");
  }
  // Enough to route the parcel within the country. Hong Kong, Singapore and
  // parts of Ireland leave the city blank and identify the destination by
  // district, region or postcode instead, so any one of these will do.
  if (!customer.city && !customer.state && !customer.zip) {
    missing.push("locality");
  }

  return missing;
}

/**
 * Returning this from `completePayment` keeps the Apple Pay sheet open so the
 * customer can correct their address, rather than failing the sheet outright.
 */
export function missingAddressError(message = MISSING_ADDRESS_MESSAGE) {
  return new ApplePayError("shippingContactInvalid", "postalAddress", message);
}
